import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { typeMeta } from '@/mocks/movies';

const values = [
  {
    icon: 'ri-bookmark-3-line',
    title: '内容严谨',
    desc: '每一部作品的剧情介绍都经过整理与核对，力求脉络清晰、信息准确。',
  },
  {
    icon: 'ri-layout-grid-line',
    title: '分类科学',
    desc: '按电影、电视剧、综艺划分，并支持类型、年份、地区等多维筛选。',
  },
  {
    icon: 'ri-refresh-line',
    title: '更新及时',
    desc: '持续收录新作品与热门剧集，让推荐列表始终保持新鲜。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '纯粹观影',
    desc: '不提供播放与下载，只专注于推荐与剧情解析，回归内容本身。',
  },
];

const team = [
  { name: '主编 · 沈砚', role: '内容策划', desc: '负责作品选题与专题策划，偏爱悬疑与年代题材。' },
  { name: '编辑 · 林一', role: '剧情撰写', desc: '专注剧情梳理与人物关系整理，让内容更易读。' },
  { name: '运营 · 周禾', role: '社区与反馈', desc: '收集观众建议，推动内容与体验持续改进。' },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于我们 - 长日光阴影视推荐平台';
    const desc = document.querySelector('meta[name="description"]');
    if (desc)
      desc.setAttribute(
        'content',
        '了解长日光阴的网站定位、发展理念与内容范围，以及我们的编辑团队与版权声明。',
      );
  }, []);

  return (
    <div className="pb-16">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">关于长日光阴</h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-700 md:text-base">
            长日光阴是一个聚焦多元影视作品推荐与剧情介绍的分享平台。我们相信，了解一个故事，往往比看完它更让人回味。因此，我们把重心放在分类、剧情与推荐上，帮助每一位观众更高效地找到想看的内容。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full px-4 md:px-6">
        <section className="grid grid-cols-1 gap-8 py-12 md:grid-cols-2">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">网站定位</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              我们面向热爱电影、电视剧与综艺的广大观众，提供一个分类清晰、信息详实的内容目录。无论你想快速了解一部作品的剧情，还是想按类型、年份、地区去发现新片，都能在这里完成。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              平台目前收录{' '}
              <span className="font-semibold text-primary-600">
                {Object.values(typeMeta).map((item) => item.label).join('、')}
              </span>{' '}
              三大类别，并持续围绕主题策划专题合集。
            </p>
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">发展理念</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              好的推荐不该让人眼花缭乱，而应让人心里有数。我们坚持"少即是多"的编辑原则，与其堆砌大量信息，不如把每一部作品的介绍写清楚、写透。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              同时，我们尊重版权与创作，站内内容均为原创整理的介绍性文本，不提供任何形式的在线播放、下载或付费服务。
            </p>
          </div>
        </section>

        <section className="py-4">
          <h2 className="font-heading text-xl font-bold text-foreground-950">内容范围</h2>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-700">
            站内作品资料目前基于虚构影视示例构建，用于展示分类、筛选、搜索与剧情阅读等完整功能。所有标题、人物与剧情均为演示内容，与真实作品无关。
          </p>
        </section>

        <section className="py-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950">我们的坚持</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-4">
          <h2 className="font-heading text-xl font-bold text-foreground-950">团队介绍</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {team.map((member) => (
              <div key={member.name} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 font-heading text-lg font-bold text-background-50">
                  {member.name.slice(0, 1)}
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{member.name}</h3>
                <p className="mt-0.5 text-xs font-medium text-accent-600">{member.role}</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{member.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="font-heading text-lg font-bold text-foreground-950">版权声明</h2>
          <p className="mt-3 text-sm leading-7 text-foreground-700">
            本站所有影视作品名称、人物与剧情均为虚构演示内容，仅用于产品功能展示，不构成对任何真实作品的指代。站内排版的原创文字内容版权归长日光阴所有，未经许可请勿转载。
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              联系我们
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              浏览作品
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}